import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Container,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Avatar,
} from "@mui/material";
import {
  FaCalendarDay,
  FaClipboardList,
  FaChartBar,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaClock,
  FaUsers,
  FaChartLine,
  FaTasks,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface FormData {
  id: string;
  title: string;
  description?: string;
  savedAt: number;
  visibleToStores?: string[];
  fields: any[];
}

interface ChecklistData {
  id: string;
  title: string;
  description?: string;
  type: string;
  savedAt: number;
  visibleToStores?: string[];
  items: any[];
}

interface DashboardStats {
  totalForms: number;
  totalChecklists: number;
  recentActivity: number;
  completionRate: number;
}

export function Ms6Page() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState<FormData[]>([]);
  const [checklists, setChecklists] = useState<ChecklistData[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalForms: 0,
    totalChecklists: 0,
    recentActivity: 0,
    completionRate: 0,
  });

  const API_BASE_URL = `${REACT_APP_DOMAIN_URL}api`;

  // Load forms from database
  const loadFormsFromDatabase = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/get_forms`
      );
      const result = await response.json();

      if (response.ok) {
        const processedForms = result.data.map((form: any) => ({
          id: form.id.toString(),
          title: form.title || "Untitled Form",
          description: form.description || "",
          savedAt: new Date(form.saved_at || form.created_at).getTime(),
          visibleToStores: form.visible_to_stores || [],
          fields: form.fields || [],
        }));
        setForms(processedForms);
        return processedForms;
      }
    } catch (error) {
      console.error("Error loading forms:", error);
    }
    return [];
  };

  // Load checklists from database
  const loadChecklistsFromDatabase = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/checklist_builder/get_checklists`
      );
      const result = await response.json();

      if (response.ok) {
        const processedChecklists = result.data.map((checklist: any) => ({
          id: checklist.id.toString(),
          title: checklist.title || "Untitled Checklist",
          description: checklist.description || "",
          type: checklist.type || "normal",
          savedAt: new Date(
            checklist.saved_at || checklist.created_at
          ).getTime(),
          visibleToStores: checklist.visible_to_stores || [],
          items: checklist.items || [],
        }));
        setChecklists(processedChecklists);
        return processedChecklists;
      }
    } catch (error) {
      console.error("Error loading checklists:", error);
    }
    return [];
  };

  // Calculate dashboard statistics
  const calculateStats = (
    formsData: FormData[],
    checklistsData: ChecklistData[]
  ) => {
    const now = Date.now();
    const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;

    const recentForms = formsData.filter((form) => form.savedAt > oneWeekAgo);
    const recentChecklists = checklistsData.filter(
      (checklist) => checklist.savedAt > oneWeekAgo
    );

    // Calculate completion rate based on checklist items
    let totalItems = 0;
    let completedItems = 0;

    checklistsData.forEach((checklist) => {
      checklist.items.forEach((item: any) => {
        totalItems++;
        if (item.completed) completedItems++;
      });
    });

    const completionRate =
      totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

    setStats({
      totalForms: formsData.length,
      totalChecklists: checklistsData.length,
      recentActivity: recentForms.length + recentChecklists.length,
      completionRate: Math.round(completionRate),
    });
  };

  // Load all data on component mount
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const [formsData, checklistsData] = await Promise.all([
          loadFormsFromDatabase(),
          loadChecklistsFromDatabase(),
        ]);
        calculateStats(formsData, checklistsData);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Chart data
  const barChartData = {
    labels: ["Forms", "Checklists", "Active Items"],
    datasets: [
      {
        label: "Count",
        data: [stats.totalForms, stats.totalChecklists, stats.recentActivity],
        backgroundColor: [
          "rgba(249, 115, 22, 0.8)",
          "rgba(239, 68, 68, 0.8)",
          "rgba(59, 130, 246, 0.8)",
        ],
        borderColor: [
          "rgba(249, 115, 22, 1)",
          "rgba(239, 68, 68, 1)",
          "rgba(59, 130, 246, 1)",
        ],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const doughnutData = {
    labels: ["Completed", "Pending"],
    datasets: [
      {
        data: [stats.completionRate, 100 - stats.completionRate],
        backgroundColor: ["rgba(34, 197, 94, 0.8)", "rgba(156, 163, 175, 0.8)"],
        borderColor: ["rgba(34, 197, 94, 1)", "rgba(156, 163, 175, 1)"],
        borderWidth: 2,
      },
    ],
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircularProgress size={60} sx={{ color: "#f97316" }} />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-all duration-500 ${
        darkMode
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
          : "bg-gradient-to-br from-orange-50 via-white to-red-50"
      }`}
    >
      {/* Header */}
      <div
        className={`border-b ${
          darkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1
                className={`text-4xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
                style={{ fontFamily: '"Bebas Neue", sans-serif' }}
              >
                <span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">
                  6Ms Dashboard
                </span>
              </h1>
              <p
                className={`mt-1 ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Manage your forms and checklists
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-3 rounded-xl transition-all duration-300 ${
                  darkMode
                    ? "bg-gray-700 text-yellow-400 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {darkMode ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/admin/ms6/checklist-builder")}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <FaPlus className="inline mr-2" />
                Create New
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div
            className={`p-6 rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Total Forms
                </p>
                <p
                  className={`text-3xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {stats.totalForms}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-xl">
                <FaClipboardList className="text-orange-600 text-xl" />
              </div>
            </div>
          </div>

          <div
            className={`p-6 rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Total Checklists
                </p>
                <p
                  className={`text-3xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {stats.totalChecklists}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-xl">
                <FaTasks className="text-red-600 text-xl" />
              </div>
            </div>
          </div>

          <div
            className={`p-6 rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Recent Activity
                </p>
                <p
                  className={`text-3xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {stats.recentActivity}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <FaChartLine className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          <div
            className={`p-6 rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p
                  className={`text-sm ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Completion Rate
                </p>
                <p
                  className={`text-3xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {stats.completionRate}%
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <FaCheckCircle className="text-green-600 text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div
            className={`p-6 rounded-2xl shadow-lg ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Overview Statistics
            </h3>
            <Bar
              data={barChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: false },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: { color: darkMode ? "#374151" : "#f3f4f6" },
                    ticks: { color: darkMode ? "#d1d5db" : "#6b7280" },
                  },
                  x: {
                    grid: { color: darkMode ? "#374151" : "#f3f4f6" },
                    ticks: { color: darkMode ? "#d1d5db" : "#6b7280" },
                  },
                },
              }}
            />
          </div>

          <div
            className={`p-6 rounded-2xl shadow-lg ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Completion Rate
            </h3>
            <div className="flex justify-center">
              <div style={{ width: "250px", height: "250px" }}>
                <Doughnut
                  data={doughnutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { color: darkMode ? "#d1d5db" : "#6b7280" },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Items */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Forms */}
          <div
            className={`p-6 rounded-2xl shadow-lg ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex justify-between items-center mb-6">
              <h3
                className={`text-xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Recent Forms
              </h3>
              <button
                onClick={() => navigate("/admin/ms6/checklist-builder")}
                className="text-orange-500 hover:text-orange-600 font-medium"
              >
                View All
              </button>
            </div>
            <div className="space-y-4">
              {forms.slice(0, 5).map((form) => (
                <div
                  key={form.id}
                  className={`p-4 rounded-xl border transition-all duration-300 hover:shadow-md ${
                    darkMode
                      ? "border-gray-600 hover:border-gray-500 bg-gray-700"
                      : "border-gray-200 hover:border-gray-300 bg-gray-50"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4
                        className={`font-semibold ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {form.title}
                      </h4>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {form.fields.length} fields • {formatDate(form.savedAt)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors">
                        <FaEye size={14} />
                      </button>
                      <button className="p-2 rounded-lg bg-orange-100 text-orange-600 hover:bg-orange-200 transition-colors">
                        <FaEdit size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {forms.length === 0 && (
                <div className="text-center py-8">
                  <FaClipboardList
                    className={`mx-auto mb-4 text-4xl ${
                      darkMode ? "text-gray-600" : "text-gray-300"
                    }`}
                  />
                  <p
                    className={`${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No forms yet. Create your first form!
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Checklists */}
          <div
            className={`p-6 rounded-2xl shadow-lg ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <div className="flex justify-between items-center mb-6">
              <h3
                className={`text-xl font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Recent Checklists
              </h3>
              <button
                onClick={() => navigate("/admin/ms6/checklist-builder")}
                className="text-red-500 hover:text-red-600 font-medium"
              >
                View All
              </button>
            </div>
            <div className="space-y-4">
              {checklists.slice(0, 5).map((checklist) => (
                <div
                  key={checklist.id}
                  className={`p-4 rounded-xl border transition-all duration-300 hover:shadow-md ${
                    darkMode
                      ? "border-gray-600 hover:border-gray-500 bg-gray-700"
                      : "border-gray-200 hover:border-gray-300 bg-gray-50"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4
                        className={`font-semibold ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {checklist.title}
                      </h4>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {checklist.items.length} items • {checklist.type} •{" "}
                        {formatDate(checklist.savedAt)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors">
                        <FaEye size={14} />
                      </button>
                      <button className="p-2 rounded-lg bg-orange-100 text-orange-600 hover:bg-orange-200 transition-colors">
                        <FaEdit size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {checklists.length === 0 && (
                <div className="text-center py-8">
                  <FaTasks
                    className={`mx-auto mb-4 text-4xl ${
                      darkMode ? "text-gray-600" : "text-gray-300"
                    }`}
                  />
                  <p
                    className={`${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No checklists yet. Create your first checklist!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
